<template>
  <div>
    <base-header class="pb-4">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">Poal - Materiales Problemáticos</h6>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button
            size="sm"
            type="neutral"
            @click="loadMateriales"
            :loading="loading"
          >
            <i class="fas fa-sync-alt mr-2"></i>Actualizar
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--4">
      <div class="row">
        <div class="col">
          <card
            class="no-border-card"
            body-classes="px-0 pb-1"
            footer-classes="pb-2"
          >
            <div class="p-2">
              <!-- Filtros -->
              <div class="row mb-3">
                <div class="col-md-3">
                  <label class="form-control-label">Filtrar por estado</label>
                  <select v-model="filtroEstado" class="form-control" @change="aplicarFiltros">
                    <option value="">Todos</option>
                    <option value="no_devuelto">No Devuelto</option>
                    <option value="consumido">Consumido</option>
                    <option value="incidencia">Con Incidencia</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label">Buscar</label>
                  <input
                    v-model="searchQuery"
                    type="text"
                    class="form-control"
                    placeholder="Buscar por albarán, material..."
                    @input="aplicarFiltros"
                  />
                </div>
              </div>

              <!-- Debug info -->
              <div v-if="materiales.length > 0" class="mb-2 text-muted small">
                Total materiales cargados: {{ materiales.length }} | 
                Materiales filtrados: {{ materialesFiltrados.length }}
              </div>
              
              <!-- Tabla de materiales -->
              <el-table
                :data="materialesPaginados"
                style="width: 100%"
                class="table-responsive align-items-center table-flush"
                header-row-class-name="thead-light"
                empty-text="No hay materiales problemáticos"
                v-loading="loading"
              >
                <el-table-column
                  label="Albarán"
                  prop="albaran.numero"
                  sortable="custom"
                  :min-width="120"
                >
                  <template slot-scope="scope">
                    <router-link
                      :to="`/albaran-management/view-albaran/${getAlbaranId(scope.row.albaran) || ''}`"
                      class="text-primary"
                    >
                      {{ getAlbaranNumero(scope.row.albaran) }}
                    </router-link>
                  </template>
                </el-table-column>

                <el-table-column
                  label="Fecha Entrega"
                  prop="albaran.fecha_entrega"
                  sortable="custom"
                  :min-width="120"
                >
                  <template slot-scope="scope">
                    {{ formatDate(scope.row.albaran?.fecha_entrega || scope.row.albaran?.attributes?.fecha_entrega) }}
                  </template>
                </el-table-column>

                <el-table-column
                  label="Jefe de Equipo"
                  prop="albaran.users_permissions_user"
                  sortable="custom"
                  :min-width="150"
                >
                  <template slot-scope="scope">
                    {{ getJefeEquipo(scope.row.albaran) }}
                  </template>
                </el-table-column>

                <el-table-column
                  label="Material"
                  :min-width="200"
                >
                  <template slot-scope="scope">
                    <div>
                      <!-- Activo -->
                      <template v-if="scope.row.activo && (scope.row.activo.id || scope.row.activo.nombre || scope.row.activo.attributes?.nombre || scope.row.activo.data?.attributes?.nombre)">
                        <strong>
                          {{ scope.row.activo?.nombre || scope.row.activo?.attributes?.nombre || scope.row.activo?.data?.attributes?.nombre || 'Sin nombre' }}
                        </strong>
                        <br />
                        <small class="text-muted">
                          {{ scope.row.activo?.numero_serie_qr || scope.row.activo?.attributes?.numero_serie_qr || scope.row.activo?.data?.attributes?.numero_serie_qr || 'Sin QR' }}
                        </small>
                      </template>
                      <!-- Saca -->
                      <template v-else-if="scope.row.es_saca || scope.row.tipo_material === 'saca'">
                        <strong>
                          {{ scope.row.nombre || scope.row.descripcion || 'Saca sin nombre' }}
                        </strong>
                        <br />
                        <small class="text-muted">
                          QR: {{ scope.row.qr || 'Sin QR' }}
                        </small>
                      </template>
                      <!-- Consumible u otro -->
                      <template v-else>
                        <strong>
                          {{ scope.row.descripcion || scope.row.nombre || scope.row.idArticulo_flxtext || `Material ID: ${scope.row.id || 'N/A'}` }}
                        </strong>
                        <br />
                        <small class="text-muted" v-if="scope.row.cantidad">
                          Cantidad: {{ scope.row.cantidad }} {{ scope.row.unidad || 'un' }}
                        </small>
                        <small class="text-muted" v-else>
                          Tipo: {{ scope.row.tipo_material || 'N/A' }}
                        </small>
                      </template>
                    </div>
                  </template>
                </el-table-column>

                <el-table-column
                  label="Tipo Material"
                  :min-width="120"
                  sortable="custom"
                >
                  <template slot-scope="scope">
                    <span :class="getTipoMaterialBadgeClass(scope.row)">
                      {{ getTipoMaterial(scope.row) }}
                    </span>
                  </template>
                </el-table-column>

                <el-table-column
                  label="Estado"
                  prop="estado"
                  sortable="custom"
                  :min-width="120"
                >
                  <template slot-scope="scope">
                    <span
                      class="badge"
                      :class="getEstadoBadgeClassPoal(scope.row.estado)"
                    >
                      {{ getEstadoTexto(scope.row.estado) }}
                    </span>
                  </template>
                </el-table-column>

                <el-table-column
                  label="Observaciones"
                  :min-width="200"
                >
                  <template slot-scope="scope">
                    <div v-if="scope.row.observaciones" class="text-truncate" style="max-width: 200px" :title="scope.row.observaciones">
                      {{ scope.row.observaciones }}
                    </div>
                    <span v-else class="text-muted">-</span>
                  </template>
                </el-table-column>

                <el-table-column
                  label="Acciones"
                  align="center"
                  :min-width="150"
                  fixed="right"
                >
                  <template slot-scope="scope">
                    <base-button
                      size="sm"
                      type="primary"
                      @click="verDetalle(scope.row)"
                      class="mr-1"
                    >
                      <i class="fas fa-eye"></i>
                    </base-button>
                    <base-button
                      v-if="scope.row.incidencias && scope.row.incidencias.length > 0"
                      size="sm"
                      type="success"
                      @click="resolverIncidencia(scope.row)"
                    >
                      <i class="fas fa-check-circle"></i> Resolver
                    </base-button>
                  </template>
                </el-table-column>
              </el-table>

            </div>
            <div
              slot="footer"
              class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
            >
              <div class="row">
                <div class="col-xl-4">
                  <el-select
                    class="select-primary pagination-select"
                    v-model="pagination.perPage"
                    placeholder="Per page"
                  >
                    <el-option
                      class="select-primary"
                      v-for="item in pagination.perPageOptions"
                      :key="item"
                      :label="item"
                      :value="item"
                    >
                    </el-option>
                  </el-select>
                </div>

                <div class="col-xl-8">
                  <p class="card-category" style="margin-top: 0.5rem">
                    Mostrando {{ total ? from + 1 : 0 }} - {{ to }} de
                    {{ total }} registros
                  </p>
                </div>
              </div>

              <base-pagination
                class="pagination-no-border"
                v-model="pagination.currentPage"
                :per-page="pagination.perPage"
                :total="total"
              />
            </div>
          </card>
        </div>
      </div>
    </div>

    <!-- Modal para ver detalle del material -->
    <modal
      :show.sync="showDetailModal"
      modal-classes="modal-dialog-centered modal-xl"
      @close="showDetailModal = false"
    >
      <template v-slot:header>
        <h3 class="modal-title">Detalle del Material</h3>
      </template>
      <div class="modal-body" v-if="materialSeleccionado">
        <div class="row">
          <div class="col-md-6">
            <h5>Información del Albarán</h5>
            <p><strong>Número:</strong> {{ materialSeleccionado.albaran?.numero || materialSeleccionado.albaran?.attributes?.numero || '-' }}</p>
            <p><strong>Fecha Entrega:</strong> {{ formatDate(materialSeleccionado.albaran?.fecha_entrega || materialSeleccionado.albaran?.attributes?.fecha_entrega) }}</p>
            <p><strong>Jefe de Equipo:</strong> {{ getJefeEquipo(materialSeleccionado.albaran) }}</p>
          </div>
          <div class="col-md-6">
            <h5>Información del Material</h5>
            <p><strong>Estado:</strong> 
              <span class="badge" :class="getEstadoBadgeClassPoal(materialSeleccionado.estado)">
                {{ getEstadoTexto(materialSeleccionado.estado) }}
              </span>
            </p>
            <p v-if="materialSeleccionado.observaciones">
              <strong>Observaciones:</strong> {{ materialSeleccionado.observaciones }}
            </p>
          </div>
        </div>
        <div class="row mt-3" v-if="materialSeleccionado.incidencias && materialSeleccionado.incidencias.length > 0">
          <div class="col-md-12">
            <h5>Incidencias</h5>
            <div
              v-for="(incidencia, index) in materialSeleccionado.incidencias"
              :key="index"
              class="card mb-2"
            >
              <div class="card-body">
                <p><strong>Tipo:</strong> 
                  {{
                    incidencia.attributes?.tipo_incidencia?.data?.attributes?.descripcion ||
                    incidencia.tipo_incidencia?.descripcion ||
                    'Sin tipo'
                  }}
                </p>
                <p><strong>Descripción:</strong> {{ incidencia.attributes?.descripcion || incidencia.descripcion || '-' }}</p>
                <p><strong>Fecha:</strong> {{ formatDate(incidencia.attributes?.createdAt || incidencia.createdAt) }}</p>
                <p v-if="incidencia.attributes?.resolucion || incidencia.resolucion">
                  <strong>Resolución:</strong> {{ incidencia.attributes?.resolucion || incidencia.resolucion }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <template v-slot:footer>
        <base-button type="secondary" @click="showDetailModal = false">
          Cerrar
        </base-button>
      </template>
    </modal>

    <!-- Modal para resolver incidencia -->
    <modal
      :show.sync="showResolveModal"
      modal-classes="modal-dialog-centered modal-lg"
      @close="showResolveModal = false"
    >
      <template v-slot:header>
        <h3 class="modal-title">Resolver Incidencia</h3>
      </template>
      <div class="modal-body" v-if="incidenciaSeleccionada">
        <div class="form-group">
          <label class="form-control-label">Comentario de Resolución</label>
          <textarea
            v-model="comentarioResolucion"
            class="form-control"
            rows="4"
            placeholder="Describe cómo se resolvió la incidencia..."
          ></textarea>
        </div>
      </div>
      <template v-slot:footer>
        <base-button type="secondary" @click="showResolveModal = false">
          Cancelar
        </base-button>
        <base-button
          type="primary"
          @click="confirmarResolucion"
          :disabled="!comentarioResolucion || loading"
        >
          Resolver Incidencia
        </base-button>
      </template>
    </modal>
  </div>
</template>

<script>
import axios from 'axios';
import { Table, TableColumn, Select, Option } from 'element-ui';
import { BasePagination } from "@/components";
import { getEstadoTexto, getEstadoBadgeClass } from '@/util/estadoColors';

export default {
  name: 'PoalPage',
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    BasePagination,
  },
  data() {
    return {
      loading: false,
      materiales: [],
      filtroEstado: '',
      searchQuery: '',
      pagination: {
        perPage: 100,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50, 100],
      },
      showDetailModal: false,
      showResolveModal: false,
      materialSeleccionado: null,
      incidenciaSeleccionada: null,
      comentarioResolucion: '',
      apiUrl: process.env.VUE_APP_API_URL || 'https://misepis-api.delfromsoftware.com/api'
    };
  },
  computed: {
    materialesFiltrados() {
      let filtrados = this.materiales;

      // Filtrar por estado
      if (this.filtroEstado) {
        if (this.filtroEstado === 'incidencia') {
          filtrados = filtrados.filter(
            (m) =>
              m.estado === 'incidencia' ||
              (m.incidencias && m.incidencias.length > 0)
          );
        } else {
          filtrados = filtrados.filter(m => m.estado === this.filtroEstado);
        }
      }

      // Filtrar por búsqueda
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtrados = filtrados.filter(m => {
          const albaranNumero = (m.albaran?.numero || m.albaran?.attributes?.numero || '').toLowerCase();
          const materialNombre = (
            m.activo?.nombre || 
            m.activo?.attributes?.nombre || 
            m.descripcion || 
            m.nombre || 
            ''
          ).toLowerCase();
          return albaranNumero.includes(query) || materialNombre.includes(query);
        });
      }

      // Ordenar por fecha de entrega (más reciente primero)
      filtrados.sort((a, b) => {
        const fechaA = a.albaran?.fecha_entrega || a.albaran?.attributes?.fecha_entrega;
        const fechaB = b.albaran?.fecha_entrega || b.albaran?.attributes?.fecha_entrega;
        
        // Si no tienen fecha, ponerlos al final
        if (!fechaA && !fechaB) return 0;
        if (!fechaA) return 1;
        if (!fechaB) return -1;
        
        // Ordenar de más reciente a más antigua (descendente)
        return new Date(fechaB) - new Date(fechaA);
      });

      return filtrados;
    },
    mostrarIncidencia() {
      return this.filtroEstado === '' || this.filtroEstado === 'incidencia';
    },
    total() {
      return this.materialesFiltrados.length;
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
    materialesPaginados() {
      const start = this.from;
      const end = this.to;
      return this.materialesFiltrados.slice(start, end);
    }
  },
  mounted() {
    this.loadMateriales();
  },
  methods: {
    getEstadoTexto,
    getEstadoBadgeClass,
    
    getAlbaranId(albaran) {
      if (!albaran) return null;
      // Intentar obtener el id de diferentes estructuras
      // Si el albarán ya tiene el id almacenado (después de la normalización), usarlo
      return albaran.id || 
             albaran.attributes?.id || 
             albaran.data?.id || 
             albaran.data?.attributes?.id ||
             null;
    },
    
    getAlbaranNumero(albaran) {
      if (!albaran) return '-';
      // Intentar obtener el número de diferentes estructuras
      return albaran.numero || 
             albaran.attributes?.numero || 
             albaran.data?.attributes?.numero ||
             albaran.data?.numero ||
             '-';
    },
    
    getTipoMaterial(material) {
      // Prioridad 1: Si es saca (por flag o por tipo_material)
      if (material.es_saca || material.tipo_material === 'saca') {
        return 'Saca';
      }
      
      // Prioridad 2: Si tiene tipo_material definido, usarlo (excepto si es 'saca' ya cubierto arriba)
      if (material.tipo_material && material.tipo_material !== 'saca') {
        // Capitalizar la primera letra
        return material.tipo_material.charAt(0).toUpperCase() + material.tipo_material.slice(1);
      }
      
      // Prioridad 3: Si tiene activo relacionado válido (no null, no vacío, con id o nombre), es un activo
      if (material.activo) {
        // Verificar que el activo sea válido (tiene id o nombre)
        const activoId = material.activo.id || material.activo.attributes?.id || material.activo.data?.id;
        const activoNombre = material.activo.nombre || material.activo.attributes?.nombre || material.activo.data?.attributes?.nombre;
        if (activoId || activoNombre) {
          return 'Activo';
        }
      }
      
      // Por defecto, es consumible
      return 'Consumible';
    },
    
    getTipoMaterialBadgeClass(material) {
      // Colores según ViewAlbaranPage (líneas 556-565):
      // Saca = badge-primary (azul), Activo = badge-default (gris/azul medio), Consumible = badge-warning (naranja)
      if (material.es_saca || material.tipo_material === 'saca') {
        return 'badge badge-primary text-white';
      }
      
      // Verificar si realmente es un activo válido
      if (material.activo) {
        const activoId = material.activo.id || material.activo.attributes?.id || material.activo.data?.id;
        const activoNombre = material.activo.nombre || material.activo.attributes?.nombre || material.activo.data?.attributes?.nombre;
        if (activoId || activoNombre) {
          return 'badge badge-default text-white';
        }
      }
      
      // Consumible (por defecto o si tipo_material es 'consumible')
      return 'badge badge-warning text-white';
    },
    
    getEstadoBadgeClassPoal(estado) {
      // Clases personalizadas para Poal con colores específicos y letra blanca
      const classes = {
        'incidencia': 'badge-incidencia-poal',
        'no_devuelto': 'badge-no-devuelto-poal',
        'consumido': 'badge-consumido-poal'
      };
      
      // Si tiene una clase personalizada, usarla; si no, usar la función general
      return classes[estado] || this.getEstadoBadgeClass(estado);
    },
    
    getAuthHeaders() {
      return { Authorization: `Bearer ${localStorage.getItem('token')}` };
    },

    async fetchStrapiAllPages(path, params = {}) {
      const pageSize = 100;
      let page = 1;
      let pageCount = 1;
      const all = [];

      while (page <= pageCount) {
        const response = await axios.get(`${this.apiUrl}${path}`, {
          params: {
            ...params,
            'pagination[page]': page,
            'pagination[pageSize]': pageSize,
          },
          headers: this.getAuthHeaders(),
        });

        const data = response.data?.data ?? response.data;
        const items = Array.isArray(data) ? data : data ? [data] : [];
        all.push(...items);

        pageCount = response.data?.meta?.pagination?.pageCount || 1;
        page += 1;
      }

      return all;
    },

    isIncidenciaActiva(inc) {
      const incData = inc?.attributes || inc;
      const estadoIncidencia = incData?.estado_incidencia;

      let estadoNombre = null;
      if (estadoIncidencia) {
        if (estadoIncidencia.data) {
          estadoNombre =
            estadoIncidencia.data.attributes?.nombre || estadoIncidencia.data.nombre;
        } else {
          estadoNombre = estadoIncidencia.attributes?.nombre || estadoIncidencia.nombre;
        }
      }

      if (!estadoNombre) return true;

      const estadoLower = estadoNombre.toLowerCase();
      return estadoLower !== 'resuelta' && estadoLower !== 'cerrada';
    },

    getActivoIdFromRelation(activoRel) {
      if (!activoRel) return null;
      return activoRel.data?.id || activoRel.id || activoRel.attributes?.id || null;
    },

    pushIncidenciaOnMaterial(materialRaw, incidenciaRaw) {
      const material = materialRaw.attributes || materialRaw;
      if (!material.incidencias) {
        material.incidencias = [];
      } else if (!Array.isArray(material.incidencias)) {
        material.incidencias = [material.incidencias];
      }

      const incId = incidenciaRaw.id || incidenciaRaw.attributes?.id;
      const yaExiste = material.incidencias.some((inc) => {
        const id = inc.id || inc.attributes?.id;
        return incId && id === incId;
      });

      if (!yaExiste) {
        material.incidencias.push(incidenciaRaw);
      }
    },

    findMaterialRawById(materiales, materialId) {
      return materiales.find((m) => {
        const id = m.id || m.attributes?.id;
        return String(id) === String(materialId);
      });
    },

    async loadMateriales() {
      this.loading = true;
      try {
        const estadosProblematicos = ['no_devuelto', 'consumido', 'incidencia'];
        const populateParams = {
          'populate[albaran_activo][populate][users_permissions_user]': '*',
          'populate[activo]': '*',
        };

        const resultados = await Promise.all(
          estadosProblematicos.map((estado) =>
            this.fetchStrapiAllPages('/albaran-activo-materials', {
              'filters[estado][$eq]': estado,
              ...populateParams,
            })
          )
        );

        let todosMateriales = resultados.flat();

        const [incidenciasAlbaranRaw, incidenciasActivoRaw] = await Promise.all([
          this.fetchStrapiAllPages('/incidencia-albarans', {
            'populate[albaran_activo][populate][users_permissions_user]': '*',
            'populate[albaran_activo_material][populate][albaran_activo][populate][users_permissions_user]': '*',
            'populate[albaran_activo_material][populate][activo]': '*',
            'populate[estado_incidencia]': '*',
          }),
          this.fetchStrapiAllPages('/incidencias', {
            'populate[estado_incidencia]': '*',
            'populate[tipo_incidencia]': '*',
            'populate[activo]': '*',
          }),
        ]);

        const incidenciasAlbaranActivas = incidenciasAlbaranRaw.filter((inc) =>
          this.isIncidenciaActiva(inc)
        );
        const incidenciasActivoActivas = incidenciasActivoRaw.filter((inc) =>
          this.isIncidenciaActiva(inc)
        );

        incidenciasAlbaranActivas.forEach((inc) => {
          const incData = inc.attributes || inc;
          const materialRel = incData.albaran_activo_material;
          if (!materialRel) return;

          const materialRaw =
            materialRel.data || (materialRel.id || materialRel.attributes ? materialRel : null);
          if (!materialRaw) return;

          const materialId = materialRaw.id || materialRaw.attributes?.id;
          if (!materialId) return;

          const existing = this.findMaterialRawById(todosMateriales, materialId);
          if (existing) {
            this.pushIncidenciaOnMaterial(existing, inc);
            return;
          }

          if (!materialRaw.attributes && materialRaw.id) {
            materialRaw.attributes = { ...materialRaw };
          }

          this.pushIncidenciaOnMaterial(materialRaw, inc);

          if (!materialRaw.attributes?.albaran_activo && incData.albaran_activo) {
            materialRaw.attributes = materialRaw.attributes || {};
            materialRaw.attributes.albaran_activo = incData.albaran_activo;
          }

          if (!materialRaw.attributes?.estado) {
            materialRaw.attributes = materialRaw.attributes || {};
            materialRaw.attributes.estado = 'incidencia';
          }

          todosMateriales.push(materialRaw);
        });

        const activoIds = [
          ...new Set(
            incidenciasActivoActivas
              .map((inc) => {
                const incData = inc.attributes || inc;
                return this.getActivoIdFromRelation(incData.activo);
              })
              .filter(Boolean)
          ),
        ];

        for (const activoId of activoIds) {
          const materialesDelActivo = await this.fetchStrapiAllPages('/albaran-activo-materials', {
            'filters[activo][id][$eq]': activoId,
            ...populateParams,
          });

          materialesDelActivo.forEach((materialRaw) => {
            const materialId = materialRaw.id || materialRaw.attributes?.id;
            if (!materialId) return;

            const incidenciasDelActivo = incidenciasActivoActivas.filter((inc) => {
              const incData = inc.attributes || inc;
              return String(this.getActivoIdFromRelation(incData.activo)) === String(activoId);
            });

            const existing = this.findMaterialRawById(todosMateriales, materialId);
            if (existing) {
              incidenciasDelActivo.forEach((inc) => this.pushIncidenciaOnMaterial(existing, inc));
              return;
            }

            incidenciasDelActivo.forEach((inc) => this.pushIncidenciaOnMaterial(materialRaw, inc));
            todosMateriales.push(materialRaw);
          });
        }

        const materialesUnicos = [];
        const idsVistos = new Set();
        todosMateriales.forEach((m) => {
          const id = m.id || m.attributes?.id;
          if (id && !idsVistos.has(id)) {
            idsVistos.add(id);
            materialesUnicos.push(m);
          }
        });

        console.log('📦 Materiales únicos para Poal:', materialesUnicos.length);
        console.log('📦 Incidencias albarán activas:', incidenciasAlbaranActivas.length);
        console.log('📦 Incidencias activo activas:', incidenciasActivoActivas.length);

        // Procesar materiales para normalizar estructura
        const materialesNormalizados = materialesUnicos.map((m, index) => {
          const material = m.attributes || m;
          
          // Debug: ver estructura original
          if (index === 0) {
            console.log('🔍 Estructura original del primer material:', m);
            console.log('🔍 Material attributes:', material);
            console.log('🔍 Albarán en material (albaran_activo):', material.albaran_activo);
            console.log('🔍 Albarán en material (albaran):', material.albaran);
          }
          
          // Normalizar incidencias
          let incidencias = [];
          if (material.incidencias) {
            if (Array.isArray(material.incidencias)) {
              incidencias = material.incidencias.map(inc => inc.attributes || inc);
            } else if (material.incidencias.data) {
              incidencias = Array.isArray(material.incidencias.data) 
                ? material.incidencias.data.map(inc => inc.attributes || inc)
                : [material.incidencias.data.attributes || material.incidencias.data];
            }
          }
          
          // Normalizar albarán - la relación se llama albaran_activo según el schema
          let albaran = null;
          let albaranId = null;
          
          // Primero intentar con albaran_activo (nombre correcto según schema)
          if (material.albaran_activo) {
            // Obtener el id del objeto original si está disponible
            albaranId = material.albaran_activo.data?.id || 
                       material.albaran_activo.id ||
                       material.albaran_activo.attributes?.id;
            
            if (material.albaran_activo.data) {
              // Estructura: albaran_activo.data.attributes o albaran_activo.data
              albaran = material.albaran_activo.data.attributes || material.albaran_activo.data;
              // Asegurar que el id esté en el objeto normalizado
              if (albaran && !albaran.id && albaranId) {
                albaran.id = albaranId;
              }
            } else if (material.albaran_activo.attributes) {
              // Estructura: albaran_activo.attributes
              albaran = material.albaran_activo.attributes;
              // Asegurar que el id esté en el objeto normalizado
              if (albaran && !albaran.id && albaranId) {
                albaran.id = albaranId;
              }
            } else if (typeof material.albaran_activo === 'object') {
              // Ya está normalizado
              albaran = material.albaran_activo;
              // Asegurar que el id esté presente
              if (albaran && !albaran.id && albaranId) {
                albaran.id = albaranId;
              }
            }
          }
          
          // Fallback: intentar con albaran (por si acaso)
          if (!albaran && material.albaran) {
            // Obtener el id del objeto original si está disponible
            albaranId = material.albaran.data?.id || 
                       material.albaran.id ||
                       material.albaran.attributes?.id;
            
            if (material.albaran.data) {
              albaran = material.albaran.data.attributes || material.albaran.data;
              // Asegurar que el id esté en el objeto normalizado
              if (albaran && !albaran.id && albaranId) {
                albaran.id = albaranId;
              }
            } else if (material.albaran.attributes) {
              albaran = material.albaran.attributes;
              // Asegurar que el id esté en el objeto normalizado
              if (albaran && !albaran.id && albaranId) {
                albaran.id = albaranId;
              }
            } else if (typeof material.albaran === 'object') {
              albaran = material.albaran;
              // Asegurar que el id esté presente
              if (albaran && !albaran.id && albaranId) {
                albaran.id = albaranId;
              }
            }
          }
          
          // Si tenemos albarán pero no tiene id, intentar obtenerlo del QR
          if (albaran && !albaran.id) {
            const qr = albaran.qr || material.albaran_activo?.qr || material.albaran?.qr;
            if (qr) {
              // Guardar el QR para obtener el id después si es necesario
              albaran._qr = qr;
            }
          }
          
          // Normalizar activo
          let activo = null;
          if (material.activo) {
            // Si activo.data es null, entonces no hay activo
            if (material.activo.data === null) {
              activo = null;
            } else if (material.activo.data) {
              // Estructura Strapi v4: activo.data.attributes o activo.data
              activo = material.activo.data.attributes || material.activo.data;
            } else if (material.activo.attributes) {
              // Estructura con attributes
              activo = material.activo.attributes;
            } else if (typeof material.activo === 'object' && material.activo.id) {
              // Ya está normalizado (tiene id)
              activo = material.activo;
            } else if (typeof material.activo === 'object' && Object.keys(material.activo).length > 0) {
              // Objeto sin estructura conocida, intentar usarlo directamente solo si no está vacío
              activo = material.activo;
            }
          }
          
          // Verificar que el activo normalizado sea válido (tiene id o nombre)
          if (activo) {
            const activoId = activo.id || activo.attributes?.id || activo.data?.id;
            const activoNombre = activo.nombre || activo.attributes?.nombre || activo.data?.attributes?.nombre;
            // Si no tiene id ni nombre, no es un activo válido
            if (!activoId && !activoNombre) {
              activo = null;
            }
          }
          
          // Debug del activo
          if (index === 0) {
            console.log('🔍 Activo original en material:', material.activo);
            console.log('🔍 Activo normalizado:', activo);
          }
          
          const materialNormalizado = {
            id: m.id || material.id,
            estado: material.estado,
            observaciones: material.observaciones,
            cantidad: material.cantidad,
            unidad: material.unidad,
            descripcion: material.descripcion,
            idArticulo_flxtext: material.idArticulo_flxtext,
            nombre: material.nombre,
            qr: material.qr,
            es_saca: material.es_saca,
            tipo_material: material.tipo_material,
            activo: activo,
            albaran: albaran,
            incidencias: incidencias
          };
          
          // Debug del primer material normalizado - información completa para columna Material
          if (index === 0) {
            console.log('✅ Material normalizado COMPLETO:', materialNormalizado);
            console.log('✅ Albarán normalizado:', materialNormalizado.albaran);
            console.log('✅ Activo normalizado:', materialNormalizado.activo);
            console.log('✅ Tipo material:', materialNormalizado.tipo_material);
            console.log('✅ Es saca:', materialNormalizado.es_saca);
            console.log('✅ Descripción:', materialNormalizado.descripcion);
            console.log('✅ Nombre:', materialNormalizado.nombre);
            console.log('✅ ID Material:', materialNormalizado.id);
            console.log('✅ QR:', materialNormalizado.qr);
            console.log('✅ Cantidad:', materialNormalizado.cantidad);
            console.log('✅ Unidad:', materialNormalizado.unidad);
            console.log('✅ idArticulo_flxtext:', materialNormalizado.idArticulo_flxtext);
            
            // Determinar tipo usando la misma lógica que getTipoMaterial
            let tipoDeterminado = 'Desconocido';
            if (materialNormalizado.es_saca || materialNormalizado.tipo_material === 'saca') {
              tipoDeterminado = 'Saca';
            } else if (materialNormalizado.tipo_material && materialNormalizado.tipo_material !== 'saca') {
              tipoDeterminado = materialNormalizado.tipo_material.charAt(0).toUpperCase() + materialNormalizado.tipo_material.slice(1);
            } else if (materialNormalizado.activo) {
              const activoId = materialNormalizado.activo.id || materialNormalizado.activo.attributes?.id || materialNormalizado.activo.data?.id;
              const activoNombre = materialNormalizado.activo.nombre || materialNormalizado.activo.attributes?.nombre || materialNormalizado.activo.data?.attributes?.nombre;
              if (activoId || activoNombre) {
                tipoDeterminado = 'Activo';
              } else {
                tipoDeterminado = 'Consumible (activo vacío/null)';
              }
            } else {
              tipoDeterminado = 'Consumible';
            }
            
            console.log('🎯 TIPO DETERMINADO:', tipoDeterminado);
            
            // Información específica para mostrar en columna Material
            console.log('📋 DATOS PARA COLUMNA MATERIAL:');
            if (tipoDeterminado === 'Activo') {
              console.log('  - Tipo: ACTIVO');
              console.log('  - Nombre activo:', materialNormalizado.activo?.nombre || materialNormalizado.activo?.attributes?.nombre);
              console.log('  - QR activo:', materialNormalizado.activo?.numero_serie_qr || materialNormalizado.activo?.attributes?.numero_serie_qr);
            } else if (tipoDeterminado === 'Saca') {
              console.log('  - Tipo: SACA');
              console.log('  - Nombre saca:', materialNormalizado.nombre);
              console.log('  - Descripción saca:', materialNormalizado.descripcion);
              console.log('  - QR saca:', materialNormalizado.qr);
            } else {
              console.log('  - Tipo: CONSUMIBLE');
              console.log('  - Descripción:', materialNormalizado.descripcion);
              console.log('  - Nombre:', materialNormalizado.nombre);
              console.log('  - idArticulo_flxtext:', materialNormalizado.idArticulo_flxtext);
              console.log('  - Cantidad:', materialNormalizado.cantidad);
              console.log('  - Unidad:', materialNormalizado.unidad);
            }
          }
          
          // Debug adicional para materiales sin activo
          if (!materialNormalizado.activo && index < 3) {
            console.log(`⚠️ Material ${index} sin activo:`, {
              id: materialNormalizado.id,
              tipo_material: materialNormalizado.tipo_material,
              es_saca: materialNormalizado.es_saca,
              descripcion: materialNormalizado.descripcion,
              nombre: materialNormalizado.nombre,
              qr: materialNormalizado.qr,
              cantidad: materialNormalizado.cantidad,
              unidad: materialNormalizado.unidad,
              idArticulo_flxtext: materialNormalizado.idArticulo_flxtext
            });
          }
          
          return materialNormalizado;
        });

        // Obtener ids de albaranes que no tengan id pero tengan QR
        const albaranesSinId = new Map();
        materialesNormalizados.forEach(m => {
          if (m.albaran && !m.albaran.id && m.albaran.qr) {
            const qr = m.albaran.qr;
            if (!albaranesSinId.has(qr)) {
              albaranesSinId.set(qr, []);
            }
            albaranesSinId.get(qr).push(m);
          }
        });

        // Si hay albaranes sin id, obtenerlos del backend
        if (albaranesSinId.size > 0) {
          console.log(`🔍 Obteniendo ids de ${albaranesSinId.size} albaranes por QR...`);
          const qrPromises = Array.from(albaranesSinId.keys()).map(async (qr) => {
            try {
              const response = await axios.get(
                `${this.apiUrl}/albaran-activos?filters[qr][$eq]=${qr}`,
                {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                  }
                }
              );
              
              const albaranes = response.data.data || response.data;
              if (Array.isArray(albaranes) && albaranes.length > 0) {
                const albaranEncontrado = albaranes[0];
                const id = albaranEncontrado.id || albaranEncontrado.attributes?.id;
                if (id) {
                  // Asignar el id a todos los materiales que usan este QR
                  const materiales = albaranesSinId.get(qr);
                  materiales.forEach(m => {
                    if (m.albaran) {
                      m.albaran.id = id;
                    }
                  });
                  return { qr, id };
                }
              } else if (albaranes && !Array.isArray(albaranes)) {
                const id = albaranes.id || albaranes.attributes?.id;
                if (id) {
                  const materiales = albaranesSinId.get(qr);
                  materiales.forEach(m => {
                    if (m.albaran) {
                      m.albaran.id = id;
                    }
                  });
                  return { qr, id };
                }
              }
            } catch (error) {
              console.warn(`Error al obtener albarán por QR ${qr}:`, error);
            }
            return null;
          });
          
          await Promise.all(qrPromises);
          console.log('✅ IDs de albaranes obtenidos');
        }

        this.materiales = materialesNormalizados;
        console.log('📦 Materiales procesados:', this.materiales);
        console.log('📦 Total materiales:', this.materiales.length);
        console.log('📦 Primer material completo:', this.materiales[0]);
        console.log('📦 Materiales filtrados:', this.materialesFiltrados.length);

        this.$notify({
          type: 'success',
          message: `Se encontraron ${this.materiales.length} materiales problemáticos`
        });
      } catch (error) {
        console.error('Error al cargar materiales:', error);
        this.$notify({
          type: 'error',
          message: 'Error al cargar los materiales problemáticos'
        });
      } finally {
        this.loading = false;
      }
    },

    aplicarFiltros() {
      this.currentPage = 1;
    },

    getJefeEquipo(albaran) {
      if (!albaran) return '-';
      const user = albaran.users_permissions_user?.data?.attributes || 
                   albaran.users_permissions_user?.attributes || 
                   albaran.users_permissions_user;
      if (user) {
        return `${user.nombre || ''} ${user.apellidos || ''}`.trim() || '-';
      }
      return '-';
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    },

    verDetalle(material) {
      this.materialSeleccionado = material;
      this.showDetailModal = true;
    },

    resolverIncidencia(material) {
      if (!material.incidencias || material.incidencias.length === 0) {
        this.$notify({
          type: 'warning',
          message: 'Este material no tiene incidencias'
        });
        return;
      }
      this.materialSeleccionado = material;
      this.incidenciaSeleccionada = material.incidencias[0];
      this.comentarioResolucion = '';
      this.showResolveModal = true;
    },

    async confirmarResolucion() {
      if (!this.comentarioResolucion.trim()) {
        this.$notify({
          type: 'warning',
          message: 'Debes agregar un comentario de resolución'
        });
        return;
      }

      this.loading = true;
      try {
        const incidenciaId = this.incidenciaSeleccionada.id || 
                            this.incidenciaSeleccionada.attributes?.id ||
                            this.incidenciaSeleccionada.data?.id;
        
        const incidenciaUpdate = {
          id: incidenciaId,
          resolucion: this.comentarioResolucion.trim(),
          estado_incidencia: {
            id: 2 // ID del estado "Resuelta"
          },
          fecha_resolucion: new Date().toISOString()
        };

        await this.$store.dispatch("incidencias/update", incidenciaUpdate);

        this.$notify({
          type: 'success',
          message: 'Incidencia resuelta correctamente'
        });

        this.showResolveModal = false;
        await this.loadMateriales();
      } catch (error) {
        console.error('Error al resolver la incidencia:', error);
        this.$notify({
          type: 'error',
          message: 'Error al resolver la incidencia'
        });
      } finally {
        this.loading = false;
      }
    },

  }
};
</script>

<style scoped>
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Badges personalizados para Poal con letra blanca */
.badge-incidencia-poal {
  background-color: #dc3545 !important; /* Rojo */
  color: #ffffff !important; /* Letra blanca */
}

.badge-no-devuelto-poal {
  background-color: #fd7e14 !important; /* Naranja */
  color: #ffffff !important; /* Letra blanca */
}

.badge-consumido-poal {
  background-color: #003366 !important; /* Azul marino */
  color: #ffffff !important; /* Letra blanca */
}
</style>
